import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, finalize, firstValueFrom, shareReplay, timeout, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ownerPropertyHasExplicitUnitTotal } from '../../shared/owner-occupancy-summary';

export type OwnerMfaChannel = 'EMAIL' | 'AUTHENTICATOR_APP' | 'SMS';

export interface MfaDeliveryCapabilities {
  smsAvailable: boolean;
  smsDestination: string | null;
}

export interface OwnerMfaInitiateResponse {
  sent?: boolean;
  channel?: string | null;
  challengeId?: string | null;
  statusCode?: number;
  message?: string;
}

export type OwnerLoginWorkspaceContext = {
  workspaceOrgId?: string | null;
  workspacePortalSlug?: string | null;
};

export type BackendPage<T> = {
  content?: T[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
};

export type OrganizationContextItem = {
  orgId: string;
  orgName: string;
  portalSlug?: string | null;
  orgType?: string | null;
  orgRole?: string | null;
  portalMode?: string | null;
  landingPath?: string | null;
  assignedPropertyIds?: string[] | null;
  owner?: boolean | null;
  subscriptionActive?: boolean | null;
  permissions?: string[] | null;
  authorizationPolicyVersion?: string | null;
  accessScopeType?: string | null;
};

export type OrganizationContextResponse = {
  hasMembership?: boolean;
  defaultOrgId?: string | null;
  organizations?: OrganizationContextItem[] | null;
};

export type WorkspaceBranding = {
  orgId: string;
  orgName: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  currency?: string | null;
  locale?: string | null;
  dateFormat?: string | null;
  timeZone?: string | null;
};

export type PropertyResponse = {
  monthlyRevenue?: number | string | null;
  id: string;
  name?: string | null;
  address?: string | null;
  totalUnits?: number | string | null;
  numberOfUnits?: number | string | null;
  actualUnits?: number | string | null;
  unitCount?: number | string | null;
  unitsCount?: number | string | null;
  occupiedUnits?: number | string | null;
  occupiedUnitCount?: number | string | null;
  vacantUnits?: number | string | null;
  coverImageUrl?: string | null;
  imageUrl?: string | null;
  images?: Array<{
    url?: string | null;
    imageUrl?: string | null;
    coverImageUrl?: string | null;
  }> | null;
  units?: Array<{
    activeLeaseId?: string | null;
    availabilityStatus?: string | null;
    leaseOccupancyStatus?: string | null;
    occupancyStatus?: string | null;
    status?: string | null;
    isOccupied?: boolean | null;
    lease?: {
      id?: string | null;
      leaseId?: string | null;
      status?: string | null;
    } | null;
  }> | null;
};

export type LeaseResponse = {
  id: string;
  propertyId?: string | null;
  propertyName?: string | null;
  unitNumber?: string | null;
  status?: string | null;
  rent?: number | null;
  monthlyTotal?: number | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type TransactionResponse = {
  id: string;
  currency?: string | null;
  propertyId?: string | null;
  amount?: number | null;
  type?: string | null;
  direction?: 'CREDIT' | 'DEBIT' | string | null;
  status?: string | null;
  reference?: string | null;
  source?: string | null;
  description?: string | null;
  transactionDate?: string | null;
  postedDate?: string | null;
  createdDate?: string | null;
};

export type DocumentResponse = {
  id: string;
  title?: string | null;
  description?: string | null;
  originalFilename?: string | null;
  category?: string | null;
  status?: string | null;
  fileUrl?: string | null;
  downloadUrl?: string | null;
  publicId?: string | null;
  contentType?: string | null;
  fileExtension?: string | null;
  sizeBytes?: number | null;
  files?: {
    id?: string | null;
    fileUrl?: string | null;
    downloadUrl?: string | null;
    publicId?: string | null;
    originalFilename?: string | null;
    contentType?: string | null;
    fileExtension?: string | null;
    sizeBytes?: number | null;
    sortOrder?: number | null;
    uploadedAt?: string | null;
  }[] | null;
  propertyId?: string | null;
  propertyName?: string | null;
  unitId?: string | null;
  unitNumber?: string | null;
  leaseId?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
  uploadedAt?: string | null;
  createdDate?: string | null;
};

export type ReportResponse = {
  id: string;
  propertyId?: string | null;
  propertyName?: string | null;
  title?: string | null;
  reportType?: string | null;
  status?: string | null;
  createdAt?: string | null;
};

export type OwnerClientResponse = {
  id: string;
  displayName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  email?: string | null;
  phone?: string | null;
  payoutProvider?: string | null;
  payoutProviderAccountId?: string | null;
  payoutsEnabled?: boolean | null;
  payoutDestinationType?: string | null;
  payoutDestinationLast4?: string | null;
  payoutDestinationBankName?: string | null;
  payoutDestinationCurrency?: string | null;
  payoutDestinationStatus?: string | null;
  payoutSchedule?: string | null;
  payoutDelayDays?: number | null;
  status?: string | null;
  version?: number | null;
};

export type OwnerEntityContext = {
  id: string;
  displayName: string;
  companyName?: string | null;
  propertyIds: string[];
};

export type OwnerSelfUpdateRequest = {
  displayName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  phone?: string | null;
  version?: number | null;
};

export type OwnerUserProfilePreferences = {
  theme?: string | null;
  darkMode?: boolean | null;
  compactMode?: boolean | null;
  preferredContactChannel?: string | null;
  density?: string | null;
  dateFormat?: string | null;
  timeFormat?: string | null;
  extra?: Record<string, unknown> | null;
};

export type OwnerUserProfile = {
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  locale?: string | null;
  profileVersion?: number | null;
  preferences?: OwnerUserProfilePreferences | null;
};

export type OwnerUserProfileUpdateRequest = {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  locale?: string | null;
  profileVersion?: number | null;
  preferences?: OwnerUserProfilePreferences | null;
};

export type OwnerPayoutOnboardingResponse = {
  ownerClientId?: string | null;
  provider?: string | null;
  providerAccountId?: string | null;
  onboardingUrl?: string | null;
  status?: string | null;
  accountCreated?: boolean | null;
  detailsSubmitted?: boolean | null;
  payoutsEnabled?: boolean | null;
  payoutDestinationType?: string | null;
  payoutDestinationLast4?: string | null;
  payoutDestinationBankName?: string | null;
  payoutDestinationCurrency?: string | null;
  payoutDestinationStatus?: string | null;
  payoutSchedule?: string | null;
  payoutDelayDays?: number | null;
  disabledReason?: string | null;
  currentlyDue?: string[] | null;
  eventuallyDue?: string[] | null;
  pastDue?: string[] | null;
};

export type OwnerPayoutPayment = {
  id: string;
  ownerClientId: string;
  propertyId?: string | null;
  payoutSettlementId?: string | null;
  amount: number;
  currency?: string | null;
  paymentMethod?: string | null;
  status: string;
  displayStatus?: string | null;
  reference?: string | null;
  notes?: string | null;
  paidAt?: string | null;
  receiptDocumentId?: string | null;
  receiptFileName?: string | null;
  receiptFileUrl?: string | null;
  ownerAcknowledgedAt?: string | null;
  ownerDisputedAt?: string | null;
  ownerDisputeMessage?: string | null;
  disputeResolvedAt?: string | null;
  disputeResolvedByUserId?: string | null;
  disputeResolutionNote?: string | null;
  reversedAt?: string | null;
  ledgerStatus?: string | null;
  ledgerFailureMessage?: string | null;
};

export type OwnerPropertyFinancialLine = {
  assignmentId: string;
  propertyId: string;
  propertyName?: string | null;
  ownershipPercentage?: number | null;
  managementFeePercentage?: number | null;
  totalCollected?: number | null;
  rentCollected: number;
  ownerGrossIncome: number;
  managementFeeAmount: number;
  ownerReserveAmount?: number | null;
  maintenanceExpenseAmount?: number | null;
  postedManagementFeeAmount?: number | null;
  pendingManagementFeeAmount?: number | null;
  netOwnerPayout: number;
  rentPaymentCount: number;
  linkedTransactionCount: number;
  pmFeePostingCount?: number | null;
  payoutSettlementCount?: number | null;
  paidPayoutSettlementCount?: number | null;
  failedPayoutSettlementCount?: number | null;
  paidOwnerPayoutAmount?: number | null;
  pendingOwnerPayoutAmount?: number | null;
};

export type OwnerFinancialSummary = {
  ownerClientId: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  feeBasis: string;
  totalCollected?: number | null;
  rentCollected: number;
  ownerGrossIncome: number;
  managementFeeAmount: number;
  ownerReserveAmount?: number | null;
  maintenanceExpenseAmount?: number | null;
  postedManagementFeeAmount?: number | null;
  pendingManagementFeeAmount?: number | null;
  netOwnerPayout: number;
  rentPaymentCount: number;
  linkedTransactionCount: number;
  pmFeePostingCount?: number | null;
  payoutSettlementCount?: number | null;
  paidPayoutSettlementCount?: number | null;
  failedPayoutSettlementCount?: number | null;
  paidOwnerPayoutAmount?: number | null;
  pendingOwnerPayoutAmount?: number | null;
  pmFeeTransactionsConnected?: boolean | null;
  payoutRecordsConnected?: boolean | null;
  properties: OwnerPropertyFinancialLine[];
};

export type OwnerFundingRequestStatus = 'REQUESTED' | 'OWNER_SENT' | 'RECEIVED' | 'CANCELLED';

export type OwnerFundingRequest = {
  id: string;
  ownerClientId: string;
  ownerName?: string | null;
  propertyId?: string | null;
  propertyName?: string | null;
  title: string;
  description?: string | null;
  amount: number;
  currency?: string | null;
  dueDate?: string | null;
  status: OwnerFundingRequestStatus;
  paymentInstructions?: string | null;
  ownerReference?: string | null;
  ownerNote?: string | null;
  requestedAt?: string | null;
  ownerSentAt?: string | null;
  receivedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  ledgerStatus?: string | null;
  ledgerTransactionId?: string | null;
  ledgerFailureMessage?: string | null;
  receiptNumber?: string | null;
  receiptEmailState?: string | null;
  version?: number | null;
};

export type OwnerMoneyTrailEvent = {
  id: string;
  type: string;
  title?: string | null;
  description?: string | null;
  occurredAt?: string | null;
  propertyId?: string | null;
  propertyName?: string | null;
  amount?: number | null;
  currency?: string | null;
  status?: string | null;
  ledgerStatus?: string | null;
  paymentOrigin?: string | null;
  provider?: string | null;
  providerTransferId?: string | null;
  source?: string | null;
  allocationBreakdown?: OwnerMoneyTrailAllocationLine[] | null;
};

export type OwnerMoneyTrailAllocationLine = {
  scheduleId?: string | null;
  chargeType?: string | null;
  dueDate?: string | null;
  amount?: number | null;
  entryType?: string | null;
};

export type OwnerMoneyTrail = OwnerFinancialSummary & {
  timeline: OwnerMoneyTrailEvent[];
  timelinePage: number;
  timelineSize: number;
  timelineTotalElements: number;
  timelineTotalPages: number;
  warnings: string[];
};

export type OwnerRentArrearsItem = {
  propertyId?: string | null;
  propertyName?: string | null;
  leaseId?: string | null;
  unitId?: string | null;
  unitNumber?: string | null;
  overdueRent?: number | null;
  overdueOtherCharges?: number | null;
  totalOverdue?: number | null;
  oldestDueDate?: string | null;
  openChargeCount?: number | null;
  daysPastDue?: number | null;
  collectionStatus?: string | null;
};

export type OwnerRentArrears = {
  outstandingBalance?: number | null;
  amountDue?: number | null;
  overdueRent?: number | null;
  overdueOtherCharges?: number | null;
  totalOverdue?: number | null;
  upcomingBalance?: number | null;
  openChargeCount?: number | null;
  oldestOverdueDate?: string | null;
  items?: OwnerRentArrearsItem[] | null;
  page?: number | null;
  size?: number | null;
  totalElements?: number | null;
  totalPages?: number | null;
};

/** Year-end owner statement (tax batch 2): an annual statement of activity, not a tax form. */
export type OwnerTaxStatementRecord = {
  statementId: string;
  ownerClientId: string;
  year: number;
  revision: number;
  status: 'PUBLISHED' | 'SUPERSEDED';
  publishedAt?: string | null;
  publishedBy?: string | null;
  currency: string;
  totalIncome: number;
  totalExpenses: number;
  payouts: number;
  closingBalance: number;
  hasActivity: boolean;
  changedFromPrevious?: boolean;
  notified?: boolean;
};

export type OwnerTaxStatementLine = {
  taxLineCode: string; form?: string | null; line?: string | null; label: string; gross: number; reversed: number; amount: number;
  /** "Override: reason" when the property manager recorded a tax category override for this line. */
  overrideNote?: string | null;
  /** True when the line's tax category still needs review; it stays in the totals. */
  needsClassification?: boolean | null;
  /** Informational: "1099 box: not determined — classify by nature ..." on other owner income (batch 2.7); there is no box field. */
  form1099BoxNote?: string | null;
};

export type OwnerTaxStatementCurrency = {
  currency: string; openingBalance: number; closingBalance: number;
  income: OwnerTaxStatementLine[]; totalIncome: number; refundsAndReversals: number;
  expenses: OwnerTaxStatementLine[]; totalExpenses: number;
  ownerContributions: number; payouts: number; payoutReversals: number; otherAdjustments: number;
  collectedInYear: number; paidOutInYear: number;
  /** Schedule E rental income + other owner income (not on Schedule E) = totalIncome (batch 2.6; absent on older responses). */
  scheduleEIncome?: number | null; otherIncome?: number | null;
  /** Income still needing classification (batch 2.7): part of totalIncome, never dropped. */
  unclassifiedIncome?: number | null;
  /** Tax batch 2.9: advance rent income held as a tenant credit (negative when received, positive when applied); absent when zero. */
  advanceRentHeld?: number | null;
  /** Tax batch 2.11: advance rent refunded to the tenant in the year (already in income as a reversal); absent when zero. */
  advanceRentRefunded?: number | null;
  properties: Array<{
    propertyId?: string | null; propertyName: string;
    ownership: Array<{ ownershipPercentage?: number | null; from?: string | null; to?: string | null }>;
    openingBalance: number; totalIncome: number; totalExpenses: number; ownerContributions: number; payouts: number;
    otherAdjustments: number; closingBalance: number; income: OwnerTaxStatementLine[]; expenses: OwnerTaxStatementLine[];
    scheduleEIncome?: number | null; otherIncome?: number | null; unclassifiedIncome?: number | null;
  }>;
};

export type OwnerTaxStatement = {
  statementId?: string | null; revision?: number | null; status: string; organizationName?: string | null;
  ownerClientId: string; ownerName?: string | null; year: number; periodStart: string; periodEnd: string;
  publishedAt?: string | null; hasActivity: boolean; currencies: OwnerTaxStatementCurrency[];
  warnings?: string[] | null; notes?: string[] | null; disclaimer?: string | null;
  /** Tax category mapping version the statement was classified with (not recorded for early statements). */
  mappingVersion?: string | null;
};

export type OwnerStatementRecord = {
  id: string;
  ownerClientId: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  status: string;
  readyForSending: boolean;
  netOwnerPayout: number;
  endingBalance: number;
  sentAt?: string | null;
  sentByUserId?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
};

export type OwnerStatementDetail = OwnerFinancialSummary & {
  ownerName?: string | null;
  ownerEmail?: string | null;
  status: string;
  readyForSending: boolean;
  maintenanceExpenseAmount?: number | null;
  endingBalance?: number | null;
  lineItems?: OwnerMoneyTrailEvent[] | null;
  maintenanceLines?: Array<{
    maintenanceRequestId: string;
    propertyId?: string | null;
    propertyName?: string | null;
    unitId?: string | null;
    title?: string | null;
    serviceDate?: string | null;
    vendorInvoiceAmount?: number | null;
    billingSourceType?: string | null;
    maintenanceMarkupPercentage?: number | null;
    ownerChargeAmount?: number | null;
    vendorInvoiceReference?: string | null;
    billingStatus?: string | null;
  }> | null;
  warnings?: string[] | null;
  generatedAt?: string | null;
};

export type OwnerMaintenanceResponse = {
  id: string;
  title?: string | null;
  requestDescription?: string | null;
  requestDate?: string | null;
  dueAt?: string | null;
  scheduledFor?: string | null;
  status?: string | null;
  propertyId?: string | null;
  propertyName?: string | null;
  unitId?: string | null;
  unitNumber?: string | null;
  ownerChargeable?: boolean | null;
  vendorInvoiceAmount?: number | null;
  billingSourceType?: string | null;
  maintenanceMarkupPercentage?: number | null;
  ownerChargeAmount?: number | null;
  vendorInvoiceReference?: string | null;
  billingNotes?: string | null;
  billingStatus?: string | null;
};

export type OwnerMaintenanceDecisionRequest = {
  notes?: string | null;
};

export type OwnerInspectionResponse = {
  id: string;
  propertyId?: string | null;
  propertyName?: string | null;
  unitId?: string | null;
  unitNumber?: string | null;
  type?: string | null;
  status?: string | null;
  title?: string | null;
  summary?: string | null;
  scheduledAt?: string | null;
  completedAt?: string | null;
  overallRating?: number | null;
  checklistCount?: number | null;
  mediaCount?: number | null;
};

export type OwnerActivityResponse = {
  id: string;
  ownerClientId: string;
  action: string;
  propertyId?: string | null;
  propertyName?: string | null;
  title?: string | null;
  description?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  actorUserId?: string | null;
  actorEmail?: string | null;
  metadataJson?: string | null;
  createdDate?: string | null;
};

export type OwnerNotificationResponse = {
  id: string;
  type: string;
  title: string;
  message: string;
  actionUrl?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  read: boolean;
  readAt?: string | null;
  createdAt: string;
};

export type OwnerNotificationPage = {
  content?: OwnerNotificationResponse[];
  totalElements?: number;
};

export type OwnerChangePasswordRequest = {
  oldPassword: string;
  password: string;
  confirmPassword: string;
};

export type OwnerResponseDto = {
  message?: string | null;
  statusCode?: number | null;
  status?: string | null;
};

@Injectable({ providedIn: 'root' })
export class OwnerPortalService {
  readonly userProfile = signal<OwnerUserProfile | null>(null);
  private readonly http = inject(HttpClient);
  private readonly propertyBase = `${environment.apiGatewayUrl}/property-service`;
  private readonly transactionBase = `${environment.apiGatewayUrl}/transactions`;
  private readonly reportBase = `${environment.apiGatewayUrl}/report-service`;
  private readonly recentOwnerLoginKey = 'apexOwnerRecentLoginAt';
  private refreshInFlight$: Observable<unknown> | null = null;
  private readonly ownerEntities = new Map<string, string>();
  private readonly ownerPropertyScopes = new Map<string, Set<string>>();

  getCurrentOwnerEntities(orgId: string): Observable<OwnerEntityContext[]> {
    return this.http.get<OwnerEntityContext[]>(`${this.propertyBase}/organizations/${orgId}/owners/me/entities`, {
      withCredentials: true,
    });
  }

  setOwnerEntityContext(orgId: string, entityId: string | null, propertyIds: string[] = []): void {
    if (entityId) {
      this.ownerEntities.set(orgId, entityId);
      this.ownerPropertyScopes.set(orgId, new Set(propertyIds));
    } else {
      this.ownerEntities.delete(orgId);
      this.ownerPropertyScopes.delete(orgId);
    }
  }

  clearOwnerEntityContext(): void {
    this.ownerEntities.clear();
    this.ownerPropertyScopes.clear();
  }

  private ownerEntityHeaders(orgId: string): HttpHeaders {
    const entityId = this.ownerEntities.get(orgId);
    return entityId ? new HttpHeaders().set('X-Owner-Entity-Id', entityId) : new HttpHeaders();
  }

  verifySession() {
    return this.http.get<{
      authenticated?: boolean | null;
      accountType?: string | null;
      roles?: string[] | null;
    }>(`${environment.apiGatewayUrl}/api/v1/user/auth-verify`, {
      withCredentials: true,
    });
  }

  refreshSession(): Observable<unknown> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post(`${environment.apiGatewayUrl}/api/v1/user/refresh`, {}, {
        responseType: 'text',
        withCredentials: true,
      }).pipe(
        timeout(8000),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refreshInFlight$;
  }

  preAuthenticate(payload: { email: string; password: string; rememberMe?: boolean } & OwnerLoginWorkspaceContext) {
    return this.http.post<{
      message?: string | null;
      userId: string;
      email?: string | null;
      mfaEnabled: boolean;
      validated: boolean;
      authenticatorConfigured?: boolean | null;
      preAuthToken?: string | null;
      challengeId?: string | null;
    }>(`${environment.apiGatewayUrl}/api/v1/user/pre-authenticate`, payload, {
      headers: this.workspaceHeaders(payload.workspaceOrgId, payload.workspacePortalSlug),
      withCredentials: true,
    });
  }

  login(payload: { email: string; password: string; rememberMe?: boolean } & OwnerLoginWorkspaceContext) {
    const body = new URLSearchParams();
    body.set('username', payload.email);
    body.set('password', payload.password);
    body.set('grant_type', 'password');
    body.set('rememberMe', String(payload.rememberMe === true));
    if (payload.workspaceOrgId) {
      body.set('workspaceOrgId', payload.workspaceOrgId);
    }
    if (payload.workspacePortalSlug) {
      body.set('workspacePortalSlug', payload.workspacePortalSlug);
    }

    return this.http.post(`${environment.apiGatewayUrl}/api/v1/user/login`, body.toString(), {
      headers: this.workspaceHeaders(payload.workspaceOrgId, payload.workspacePortalSlug)
        .set('Content-Type', 'application/x-www-form-urlencoded'),
      withCredentials: true,
    });
  }

  getMfaDeliveryCapabilities(payload: { userId: string; preAuthToken: string; purpose: 'LOGIN' }) {
    return this.http.post<MfaDeliveryCapabilities>(
      `${environment.apiGatewayUrl}/api/v1/user/mfa/delivery-capabilities`, payload, {
        withCredentials: true,
      },
    ).pipe(
      timeout(8000),
      map((response) => ({
        smsAvailable: response?.smsAvailable === true,
        smsDestination: response?.smsAvailable === true && typeof response.smsDestination === 'string'
          ? response.smsDestination : null,
      })),
      catchError(() => of({ smsAvailable: false, smsDestination: null })),
    );
  }

  initiateEmailMfa(payload: { challengeId?: string | null; userId?: string | null }) {
    return this.initiateMfa({ ...payload, channel: 'EMAIL' });
  }

  initiateMfa(payload: { preAuthToken?: string | null; channel: OwnerMfaChannel; challengeId?: string | null; userId?: string | null }) {
    const body: Record<string, unknown> = {
      purpose: 'LOGIN',
      channel: payload.channel,
    };
    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }

    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<OwnerMfaInitiateResponse>(`${environment.apiGatewayUrl}/api/v1/user/send-token`, body, {
      withCredentials: true,
    }).pipe(
      map((response) => {
        const confirmed = response?.statusCode === 200
          || (response?.statusCode === undefined && response?.sent === true);
        if (!confirmed || response?.sent === false) {
          throw new Error(response?.message || 'We could not send your security code.');
        }
        return { ...response, sent: true, channel: response.channel ?? payload.channel };
      }),
    );
  }

  verifyEmailMfa(payload: {
    otpCode: string;
    challengeId?: string | null;
    userId?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  }) {
    return this.verifyMfa({ ...payload, channel: 'EMAIL' });
  }

  verifyMfa(payload: {
    otpCode: string;
    channel: OwnerMfaChannel;
    challengeId?: string | null;
    userId?: string | null;
    email?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  } & OwnerLoginWorkspaceContext) {
    const body: Record<string, unknown> = {
      otpCode: payload.otpCode,
      purpose: 'LOGIN',
      channel: payload.channel,
      rememberMe: payload.rememberMe === true,
    };
    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }
    if (payload.email) {
      body['email'] = payload.email;
    }
    if (payload.workspaceOrgId) {
      body['workspaceOrgId'] = payload.workspaceOrgId;
    }
    if (payload.workspacePortalSlug) {
      body['workspacePortalSlug'] = payload.workspacePortalSlug;
    }
    // Proves this code belongs to the password step that started the login.
    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<{ message?: string | null; statusCode: number }>(`${environment.apiGatewayUrl}/api/v1/user/verify`, body, {
      headers: this.workspaceHeaders(payload.workspaceOrgId, payload.workspacePortalSlug),
      withCredentials: true,
    });
  }

  private workspaceHeaders(workspaceOrgId?: string | null, workspacePortalSlug?: string | null): HttpHeaders {
    let headers = new HttpHeaders();
    if (workspaceOrgId) {
      headers = headers.set('X-Workspace-Org-Id', workspaceOrgId);
    }
    if (workspacePortalSlug) {
      headers = headers.set('X-Workspace-Portal-Slug', workspacePortalSlug);
    }
    return headers;
  }

  logout(): Observable<string> {
    return this.http.post(`${environment.apiGatewayUrl}/api/v1/user/logout`, {}, {
      responseType: 'text',
      withCredentials: true,
    });
  }

  markOwnerLoginVerified(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.setItem(this.recentOwnerLoginKey, String(Date.now()));
    } catch {
      // Storage is best-effort only.
    }
  }

  clearOwnerLoginState(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.removeItem(this.recentOwnerLoginKey);
      window.sessionStorage.removeItem('selectedOwnerOrgId');
    } catch {
      // Storage is best-effort only.
    }
  }

  hasRecentOwnerLoginGrace(maxAgeMs = 10000): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const timestamp = Number(window.sessionStorage.getItem(this.recentOwnerLoginKey) || '0');
      return Number.isFinite(timestamp) && timestamp > 0 && Date.now() - timestamp <= maxAgeMs;
    } catch {
      return false;
    }
  }

  getOrganizationContext(): Observable<OrganizationContextResponse> {
    return this.http.get<OrganizationContextResponse>(`${this.propertyBase}/organization/context`, {
      withCredentials: true,
    });
  }

  getBranding(orgId: string): Observable<WorkspaceBranding> {
    // The organization controller is mounted at /organization (singular); /organizations/{id}/branding is a 404.
    return this.http.get<WorkspaceBranding>(`${this.propertyBase}/organization/${orgId}/branding`, {
      withCredentials: true,
    });
  }

  listProperties(orgId: string): Observable<BackendPage<PropertyResponse>> {
    const params = new HttpParams().set('size', '100').set('sort', 'name,asc');
    const path = this.ownerEntities.has(orgId) ? 'owners/me/properties' : 'properties';
    return this.http.get<BackendPage<PropertyResponse>>(`${this.propertyBase}/organizations/${orgId}/${path}`, {
      params,
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  browseProperties(orgId: string, filters: { page: number; size: number; q: string; status: string; occupancy: string; sort: string }) {
    const owner = this.ownerEntities.has(orgId);
    const path = owner ? 'owners/me/properties/browse' : 'properties/browse';
    const params = { ...filters, ...(!owner ? {
      status: filters.status === 'ACTIVE' ? 'OWNER_ACTIVE' : filters.status === 'VACANCY' ? 'ALL' : filters.status,
      vacant: filters.status === 'VACANCY', occupancy: filters.occupancy === 'ALL' ? 'ALL' : `OWNER_${filters.occupancy}`,
    } : {}) };
    return this.http.get<BackendPage<PropertyResponse> & { number: number; summary: Record<string, number> }>(
      `${this.propertyBase}/organizations/${orgId}/${path}`, { params, headers: this.ownerEntityHeaders(orgId), withCredentials: true });
  }

  getProperty(orgId: string, propertyId: string, includeUnits = false): Observable<PropertyResponse> {
    const params = includeUnits ? new HttpParams().set('includeUnits', 'true') : undefined;
    const path = this.ownerEntities.has(orgId) ? 'owners/me/properties' : 'properties';
    return this.http.get<PropertyResponse>(`${this.propertyBase}/organizations/${orgId}/${path}/${propertyId}`, {
      params,
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  async hydrateMissingPropertyUnitCounts(orgId: string, properties: PropertyResponse[]): Promise<PropertyResponse[]> {
    if (!properties.length) return properties;

    return Promise.all(
      properties.map(async (property) => {
        if (!property.id || ownerPropertyHasExplicitUnitTotal(property)) {
          return property;
        }

        try {
          const detail = await firstValueFrom(this.getProperty(orgId, property.id, true));
          return this.mergePropertyUnitDetail(property, detail);
        } catch {
          return property;
        }
      }),
    );
  }

  listLeases(orgId: string): Observable<BackendPage<LeaseResponse>> {
    const scope = this.ownerPropertyScopes.get(orgId);
    if (scope && !scope.size) return of({ content: [], totalElements: 0, totalPages: 0 });
    const params = new HttpParams().set('size', '100').set('sort', 'startDate,desc');
    return this.http.get<BackendPage<LeaseResponse>>(`${this.propertyBase}/organizations/${orgId}/leases`, {
      params,
      withCredentials: true,
    }).pipe(map((page) => scope
      ? { ...page, content: (page.content ?? []).filter((lease) => !!lease.propertyId && scope.has(lease.propertyId)) }
      : page));
  }

  listTransactions(orgId: string, propertyIds: string[], page = 0, size = 20): Observable<BackendPage<TransactionResponse>> {
    const scope = this.ownerPropertyScopes.get(orgId);
    if (scope) propertyIds = propertyIds.filter((id) => scope.has(id));
    if (scope && !propertyIds.length) return of({ content: [], totalElements: 0, totalPages: 0 });
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'transactionDate,desc,postedDate,desc,createdDate,desc');
    propertyIds.forEach((id) => {
      params = params.append('propertyIds', id);
    });
    return this.http.get<BackendPage<TransactionResponse>>(`${this.transactionBase}/organizations/${orgId}/transactions`, {
      params,
      withCredentials: true,
    });
  }

  listDocuments(orgId: string, propertyIds: string[], page = 0, size = 20): Observable<BackendPage<DocumentResponse>> {
    if (this.ownerEntities.has(orgId)) return this.getCurrentOwnerDocuments(orgId, page, size);
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'uploadedAt,desc');
    propertyIds.forEach((id) => {
      params = params.append('propertyIds', id);
    });
    return this.http.get<BackendPage<DocumentResponse>>(`${this.propertyBase}/organizations/${orgId}/documents`, {
      params,
      withCredentials: true,
    });
  }

  listReports(orgId: string, propertyIds: string[]): Observable<BackendPage<ReportResponse>> {
    const scope = this.ownerPropertyScopes.get(orgId);
    if (scope) propertyIds = propertyIds.filter((id) => scope.has(id));
    if (scope && !propertyIds.length) return of({ content: [], totalElements: 0, totalPages: 0 });
    let params = new HttpParams().set('page', '0').set('size', '5').set('sort', 'createdAt,desc');
    propertyIds.forEach((id) => {
      params = params.append('propertyIds', id);
    });
    return this.http.get<BackendPage<ReportResponse>>(`${this.reportBase}/organizations/${orgId}/reports`, {
      params,
      withCredentials: true,
    });
  }

  getCurrentOwner(orgId: string): Observable<OwnerClientResponse> {
    return this.http.get<OwnerClientResponse>(`${this.propertyBase}/organizations/${orgId}/owners/me`, {
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  updateCurrentOwner(orgId: string, payload: OwnerSelfUpdateRequest): Observable<OwnerClientResponse> {
    return this.http.patch<OwnerClientResponse>(`${this.propertyBase}/organizations/${orgId}/owners/me`, payload, {
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  getUserProfile(): Observable<OwnerUserProfile> {
    return this.http.get<OwnerUserProfile>(`${environment.apiGatewayUrl}/api/v1/profile`, {
      withCredentials: true,
    }).pipe(tap(profile => this.userProfile.set(profile)));
  }

  updateUserProfile(payload: OwnerUserProfileUpdateRequest): Observable<OwnerUserProfile> {
    return this.http.patch<OwnerUserProfile>(`${environment.apiGatewayUrl}/api/v1/profile`, payload, {
      withCredentials: true,
    }).pipe(tap(profile => this.userProfile.set(profile)));
  }

  startOwnerPayoutOnboarding(
    orgId: string,
    payload: { returnUrl?: string | null; refreshUrl?: string | null },
  ): Observable<OwnerPayoutOnboardingResponse> {
    return this.http.post<OwnerPayoutOnboardingResponse>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/payout-onboarding`,
      payload,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  refreshOwnerPayoutOnboarding(orgId: string): Observable<OwnerPayoutOnboardingResponse> {
    return this.http.post<OwnerPayoutOnboardingResponse>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/payout-onboarding/refresh`,
      {},
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  getCurrentOwnerPayoutPayments(orgId: string, from?: string | null, to?: string | null): Observable<OwnerPayoutPayment[]> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return this.http.get<OwnerPayoutPayment[]>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/payout-payments`,
      { headers: this.ownerEntityHeaders(orgId), params, withCredentials: true },
    );
  }

  confirmCurrentOwnerPayoutPayment(orgId: string, paymentId: string): Observable<OwnerPayoutPayment> {
    return this.http.post<OwnerPayoutPayment>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/payout-payments/${paymentId}/confirm`,
      {},
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  disputeCurrentOwnerPayoutPayment(orgId: string, paymentId: string, notes: string): Observable<OwnerPayoutPayment> {
    return this.http.post<OwnerPayoutPayment>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/payout-payments/${paymentId}/dispute`,
      { notes },
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  getCurrentOwnerFinancialSummary(orgId: string): Observable<OwnerFinancialSummary> {
    return this.http.get<OwnerFinancialSummary>(`${this.propertyBase}/organizations/${orgId}/owners/me/financial-summary`, {
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  getCurrentOwnerMoneyTrail(orgId: string, timelinePage = 0, timelineSize = 10, propertyId?: string | null): Observable<OwnerMoneyTrail> {
    let params = new HttpParams()
      .set('timelinePage', String(timelinePage))
      .set('timelineSize', String(timelineSize));
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<OwnerMoneyTrail>(`${this.propertyBase}/organizations/${orgId}/owners/me/money-trail`, {
      headers: this.ownerEntityHeaders(orgId),
      params,
      withCredentials: true,
    });
  }

  getCurrentOwnerRentArrears(orgId: string, page = 0, size = 10, propertyId?: string | null): Observable<OwnerRentArrears> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<OwnerRentArrears>(`${this.propertyBase}/organizations/${orgId}/owners/me/rent-arrears`, {
      headers: this.ownerEntityHeaders(orgId),
      params,
      withCredentials: true,
    });
  }

  getCurrentOwnerFundingRequests(
    orgId: string,
    page = 0,
    size = 10,
    status?: OwnerFundingRequestStatus | null,
    propertyId?: string | null,
  ): Observable<BackendPage<OwnerFundingRequest>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (status) params = params.set('status', status);
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<BackendPage<OwnerFundingRequest>>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/funding-requests`,
      { headers: this.ownerEntityHeaders(orgId), params, withCredentials: true },
    );
  }

  downloadFundingReceipt(orgId: string, id: string): Observable<Blob> {
    return this.http.get(`${this.propertyBase}/organizations/${orgId}/owners/me/funding-requests/${id}/receipt`,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true, responseType: 'blob' });
  }

  emailFundingReceipt(orgId: string, id: string): Observable<void> {
    return this.http.post<void>(`${this.propertyBase}/organizations/${orgId}/owners/me/funding-requests/${id}/receipt/email`, {},
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true });
  }

  markCurrentOwnerFundingSent(
    orgId: string,
    requestId: string,
    request: { reference?: string | null; note?: string | null },
  ): Observable<OwnerFundingRequest> {
    return this.http.post<OwnerFundingRequest>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/funding-requests/${requestId}/mark-sent`,
      request,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  getCurrentOwnerStatements(orgId: string): Observable<OwnerStatementRecord[]> {
    return this.http.get<OwnerStatementRecord[]>(`${this.propertyBase}/organizations/${orgId}/owners/me/statements`, {
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  getCurrentOwnerStatement(orgId: string, statementId: string): Observable<OwnerStatementDetail> {
    return this.http.get<OwnerStatementDetail>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/statements/${statementId}`,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  getCurrentOwnerStatementExportUrl(orgId: string, statementId: string): string {
    const entityId = this.ownerEntities.get(orgId);
    const query = entityId ? `?ownerEntityId=${encodeURIComponent(entityId)}` : '';
    return `${this.propertyBase}/organizations/${orgId}/owners/me/statements/${statementId}/export${query}`;
  }

  getCurrentOwnerTaxStatements(orgId: string): Observable<OwnerTaxStatementRecord[]> {
    return this.http.get<OwnerTaxStatementRecord[]>(`${this.propertyBase}/organizations/${orgId}/owners/me/tax-statements`, {
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  getCurrentOwnerTaxStatement(orgId: string, statementId: string): Observable<OwnerTaxStatement> {
    return this.http.get<OwnerTaxStatement>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/tax-statements/${encodeURIComponent(statementId)}`,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  downloadCurrentOwnerTaxStatement(orgId: string, statementId: string, format: 'pdf' | 'csv'): Observable<Blob> {
    return this.http.get(
      `${this.propertyBase}/organizations/${orgId}/owners/me/tax-statements/${encodeURIComponent(statementId)}/${format}`,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true, responseType: 'blob' },
    );
  }

  getCurrentOwnerMaintenance(orgId: string, page = 0, size = 20, propertyId?: string | null): Observable<BackendPage<OwnerMaintenanceResponse>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<BackendPage<OwnerMaintenanceResponse>>(`${this.propertyBase}/organizations/${orgId}/owners/me/maintenance`, {
      headers: this.ownerEntityHeaders(orgId),
      params,
      withCredentials: true,
    });
  }

  getCurrentOwnerApprovals(orgId: string, page = 0, size = 25, propertyId?: string | null): Observable<BackendPage<OwnerMaintenanceResponse>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<BackendPage<OwnerMaintenanceResponse>>(`${this.propertyBase}/organizations/${orgId}/owners/me/maintenance/approvals`, {
      headers: this.ownerEntityHeaders(orgId), params, withCredentials: true });
  }

  approveCurrentOwnerMaintenance(
    orgId: string,
    maintenanceRequestId: string,
    request: OwnerMaintenanceDecisionRequest = {},
  ): Observable<OwnerMaintenanceResponse> {
    return this.http.post<OwnerMaintenanceResponse>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/maintenance/${maintenanceRequestId}/approve`,
      request,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  rejectCurrentOwnerMaintenance(
    orgId: string,
    maintenanceRequestId: string,
    request: OwnerMaintenanceDecisionRequest,
  ): Observable<OwnerMaintenanceResponse> {
    return this.http.post<OwnerMaintenanceResponse>(
      `${this.propertyBase}/organizations/${orgId}/owners/me/maintenance/${maintenanceRequestId}/reject`,
      request,
      { headers: this.ownerEntityHeaders(orgId), withCredentials: true },
    );
  }

  getCurrentOwnerInspections(orgId: string, page = 0, size = 20, propertyId?: string | null): Observable<BackendPage<OwnerInspectionResponse>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<BackendPage<OwnerInspectionResponse>>(`${this.propertyBase}/organizations/${orgId}/owners/me/inspections`, {
      headers: this.ownerEntityHeaders(orgId),
      params,
      withCredentials: true,
    });
  }

  getCurrentOwnerDocuments(orgId: string, page = 0, size = 20, query?: string | null, propertyId?: string | null): Observable<BackendPage<DocumentResponse>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'uploadedAt,desc');
    if (query?.trim()) params = params.set('query', query.trim());
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<BackendPage<DocumentResponse>>(`${this.propertyBase}/organizations/${orgId}/owners/me/documents`, {
      headers: this.ownerEntityHeaders(orgId),
      params,
      withCredentials: true,
    });
  }

  getCurrentOwnerActivity(orgId: string, page = 0, size = 10): Observable<BackendPage<OwnerActivityResponse>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<BackendPage<OwnerActivityResponse>>(`${this.propertyBase}/organizations/${orgId}/owners/me/activity`, {
      headers: this.ownerEntityHeaders(orgId),
      params,
      withCredentials: true,
    });
  }

  listNotifications(orgId: string, size = 10, page = 0): Observable<OwnerNotificationPage> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<OwnerNotificationPage>(`${this.propertyBase}/organization/${orgId}/notifications`, {
      params,
      withCredentials: true,
    });
  }

  unreadNotificationCount(orgId: string): Observable<{ unreadCount: number }> {
    return this.http.get<{ unreadCount: number }>(`${this.propertyBase}/organization/${orgId}/notifications/unread-count`, {
      withCredentials: true,
    });
  }

  markNotificationRead(orgId: string, notificationId: string): Observable<OwnerNotificationResponse> {
    return this.http.patch<OwnerNotificationResponse>(`${this.propertyBase}/organization/${orgId}/notifications/${notificationId}/read`, {}, {
      withCredentials: true,
    });
  }

  markAllNotificationsRead(orgId: string): Observable<{ unreadCount: number }> {
    return this.http.post<{ unreadCount: number }>(`${this.propertyBase}/organization/${orgId}/notifications/read-all`, {}, {
      withCredentials: true,
    });
  }

  notificationStreamUrl(orgId: string): string {
    return `${this.propertyBase}/organization/${orgId}/notifications/stream`;
  }

  requestEmailChange(orgId: string, payload: { requestedEmail: string; reason?: string | null }): Observable<OwnerResponseDto> {
    return this.http.post<OwnerResponseDto>(`${this.propertyBase}/organizations/${orgId}/owners/me/email-change-request`, payload, {
      headers: this.ownerEntityHeaders(orgId),
      withCredentials: true,
    });
  }

  changePassword(payload: OwnerChangePasswordRequest): Observable<OwnerResponseDto> {
    return this.http.put<OwnerResponseDto>(`${environment.apiGatewayUrl}/api/v1/profile/change-password`, payload, {
      withCredentials: true,
    });
  }

  private mergePropertyUnitDetail(summary: PropertyResponse, detail: PropertyResponse | null | undefined): PropertyResponse {
    if (!detail) return summary;

    return {
      ...summary,
      ...detail,
      id: summary.id,
      name: detail.name ?? summary.name,
      address: detail.address ?? summary.address,
      coverImageUrl: detail.coverImageUrl ?? summary.coverImageUrl,
      imageUrl: detail.imageUrl ?? summary.imageUrl,
      images: detail.images?.length ? detail.images : summary.images,
      units: Array.isArray(detail.units) ? detail.units : summary.units,
    };
  }
}
